/**
 * ProtectedRoute Component
 * 
 * Wraps content and checks permissions before rendering.
 * If user lacks required permissions, shows unauthorized message.
 * 
 * @example
 * ```tsx
 * <ProtectedRoute requiredPermission="data_management.read">
 *   <DataManagementDashboard />
 * </ProtectedRoute>
 * ```
 */

import React from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { Shield, AlertCircle } from 'lucide-react';
import { Card } from '../UI/Card';

interface ProtectedRouteProps {
  /** Required permission(s) - can be single permission or array (OR logic) */
  requiredPermission?: string | string[];
  /** Required permissions (AND logic - user must have all) */
  requiredAllPermissions?: string[];
  /** Children to render if authorized */
  children: React.ReactNode;
  /** Fallback component to render if unauthorized (optional) */
  fallback?: React.ReactNode;
  /** Show loading state while checking permissions */
  showLoading?: boolean;
}

export function ProtectedRoute({
  requiredPermission,
  requiredAllPermissions,
  children,
  fallback,
  showLoading = true,
}: ProtectedRouteProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  // Show loading state
  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Check permissions
  let authorized = true;

  if (requiredPermission) {
    if (Array.isArray(requiredPermission)) {
      // OR logic - user needs at least one permission
      authorized = hasAnyPermission(requiredPermission);
    } else {
      // Single permission
      authorized = hasPermission(requiredPermission);
    }
  }

  if (requiredAllPermissions && requiredAllPermissions.length > 0) {
    // AND logic - user needs all permissions
    authorized = authorized && hasAllPermissions(requiredAllPermissions);
  }

  // If not authorized, show fallback or default unauthorized message
  if (!authorized) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You don't have permission to access this section.
            </p>
            {requiredPermission && (
              <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="text-left">
                    <p className="font-medium mb-1">Required permission(s):</p>
                    <ul className="list-disc list-inside space-y-1">
                      {Array.isArray(requiredPermission) ? (
                        requiredPermission.map((perm, idx) => (
                          <li key={idx}>{perm}</li>
                        ))
                      ) : (
                        <li>{requiredPermission}</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
              Please contact your administrator if you believe you should have access.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Authorized - render children
  return <>{children}</>;
}


